import { update as updateTween } from 'three/examples/jsm/libs/tween.module';
import { ComponentInteractionType, SceneComponent } from 'src/sdk-components/SceneComponent';
import { LoadingState } from 'src/sdk-components/VSObject';

import { Label } from 'src/service/navigation/component/Label';
import {
  getAzimuthAngle,
  calculateSubsegments,
  getCurveLength,
} from 'src/service/navigation/utils';
import { stepsInSameTeleportGroup } from 'src/service/navigation/utils';
import { NavigationType } from 'src/service/navigation/types';

export class PathFinderLineComponent extends SceneComponent {
  public THREE: any;

  public root: any = null;

  public pivot: any = null;

  public line: any;

  public texture: any;

  public sphereArr: any = [];

  public animationId: any = null;

  private frustum: any;

  private leftHelperSphere: any;

  private rightHelperSphere: any;

  private leftHelperSphereWorld: any;

  private rightHelperSphereWorld: any;

  private pathSphereHelper: any;

  private cameraWorld: any;

  private cameraWorldQuaternion: any;

  private cameraViewProjectionMatrix: any;

  public isCameraFocused: any;

  public cameraDirectionVector: any;

  public horizonVector: any;

  public calculatedCameraDirectionVector: any;

  private textureLoader: any;

  private directionTextures: { left: any; right: any } = { left: null, right: null };

  public pathDirectionSigns: any = [];

  inputs: any = {
    camera: null,
    path: [],
    pathOffsetY: 0,
    targetObjectData: null,
    visible: true,
    color: 'white',
    arrowRef: null,
    teleportPoints: [],
    onDistanceChanged: null,
    navigationType: NavigationType.Shortest,
    navbarPathColor: null,
  };

  outputs: any = {
    loadingState: LoadingState.Idle,
  };

  events = {
    [ComponentInteractionType.CLICK]: false,
    [ComponentInteractionType.HOVER]: false,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  async onInit() {
    const THREE = this.context.three;
    this.frustum = new THREE.Frustum();
    this.cameraViewProjectionMatrix = new THREE.Matrix4();
    this.cameraDirectionVector = new THREE.Vector3();
    this.horizonVector = new THREE.Vector3();
    this.calculatedCameraDirectionVector = new THREE.Vector3();
    this.root = new THREE.Object3D();
    this.root.visible = this.inputs.visible;
    this.pivot = new THREE.Object3D();
    this.textureLoader = new THREE.TextureLoader();
    this.root.add(this.pivot);
    this.outputs.objectRoot = this.root;
    this.createHelperSpheres();
    await this.preloadAllTextures();

    if (this.inputs.path.length) {
      this.createLineFromPath(this.inputs.path);
    }
  }

  private loadTextureAsync(url: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      this.textureLoader.load(url, resolve, undefined, reject);
    });
  }

  async preloadAllTextures() {
    const [leftTexture, rightTexture, pathSphereTexture] = await Promise.all([
      this.loadTextureAsync('/images/turn-sign-left.png'),
      this.loadTextureAsync('/images/turn-sign-right.png'),
      this.loadTextureAsync('/images/path-sphere.png'),
    ]);

    this.directionTextures.left = leftTexture;
    this.directionTextures.right = rightTexture;
    this.texture = pathSphereTexture;

    console.log('All textures have been preloaded.');
  }

  createHelperSpheres() {
    const THREE = this.context.three;
    const geometry = new THREE.SphereGeometry(0.1, 32, 16);
    const material = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    this.leftHelperSphere = new THREE.Mesh(geometry, material);
    this.rightHelperSphere = new THREE.Mesh(geometry, material);

    this.leftHelperSphere.scale.setScalar(0);
    this.rightHelperSphere.scale.setScalar(0);

    this.leftHelperSphere.position.x = -0.75;
    this.leftHelperSphere.position.z = -1;
    this.rightHelperSphere.position.x = 0.75;
    this.rightHelperSphere.position.z = -1;
    this.inputs.camera.add(this.leftHelperSphere);
    this.inputs.camera.add(this.rightHelperSphere);

    this.pathSphereHelper = new THREE.Vector3();
    this.leftHelperSphereWorld = new THREE.Vector3();
    this.rightHelperSphereWorld = new THREE.Vector3();
    this.cameraWorld = new THREE.Vector3();
    this.cameraWorldQuaternion = new THREE.Quaternion();
  }

  createLineFromPath(matterPath) {
    const THREE = this.context.three;
    let fullDistance = 0;
    const spacingFactor = 1.2;
    for (let i = 0; i < matterPath.length; i++) {
      if (matterPath[i] && matterPath[i + 1]) {
        if (
          this.inputs.teleportPoints.length &&
          this.inputs.navigationType !== NavigationType.NoStairs
        ) {
          const skipIteration = stepsInSameTeleportGroup(
            matterPath[i],
            matterPath[i + 1],
            this.inputs.teleportPoints,
          );
          // eslint-disable-next-line no-continue
          if (skipIteration) continue;
        }
        const start = new THREE.Vector3(
          matterPath[i].data.position.x,
          matterPath[i].data.position.y + this.inputs.pathOffsetY,
          matterPath[i].data.position.z,
        );
        const end = new THREE.Vector3(
          matterPath[i + 1].data.position.x,
          matterPath[i + 1].data.position.y + this.inputs.pathOffsetY,
          matterPath[i + 1].data.position.z,
        );
        const path = new THREE.CatmullRomCurve3([start, end]);

        const distance = getCurveLength(path);
        fullDistance += distance;
        const { subsegments } = calculateSubsegments(distance, spacingFactor);
        this.createSubSegments(subsegments, path, i === matterPath.length - 2);
      }
    }

    this.outputs.loadingState = LoadingState.Loaded;
    this.emitDistanceEvent(fullDistance);
  }

  createSubSegments(count, path, isLastSegment) {
    const THREE = this.context.three;

    if (!this.texture) {
      console.warn('Path sphere texture not loaded yet, skipping segment creation');
      return;
    }

    const planeGeometry = new THREE.PlaneGeometry(0.13, 0.13);
    const sphereColor = new THREE.Color(this.inputs.navbarPathColor || '#39b6fe');

    for (let i = 0; i < count; i++) {
      const start = path.getPointAt(i / count);
      const end = path.getPointAt((i + 1) / count);
      const curve = new THREE.CatmullRomCurve3([start, end]);
      const startPoint = curve.getPointAt(0);
      const endPoint = curve.getPointAt(1);

      const planeMaterial = new THREE.MeshBasicMaterial({
        color: sphereColor,
        map: this.texture,
        side: THREE.DoubleSide,
        transparent: true,
      });
      planeMaterial.depthWrite = false;
      // Create a sphere at the start point
      const startSphere = new THREE.Mesh(planeGeometry, planeMaterial);

      startSphere.position.copy(startPoint);
      this.pivot.add(startSphere);
      this.sphereArr.push(startSphere);

      // Create a sphere at the end point of the last segment
      if (i === count - 1 && isLastSegment) {
        const planeMaterialCopy = new THREE.MeshBasicMaterial({
          color: sphereColor,
          map: this.texture,
          side: THREE.DoubleSide,
          transparent: true,
        });
        const endSphere = new THREE.Mesh(planeGeometry, planeMaterialCopy);
        endSphere.position.copy(endPoint);
        this.pivot.add(endSphere);
        this.sphereArr.push(endSphere);
      }
    }
  }

  createPathDirectionSigns(path) {
    if (!this.inputs.path?.length || !this.inputs.targetObjectData) return;
    const THREE = this.context.three;
    const cameraPos = this.inputs.camera?.parent.position;
    if (!cameraPos) {
      console.error('Camera is not defined');
      return;
    }
    const pointNodes = this.inputs.teleportPoints?.flatMap((tp) => tp.pointNodes);

    path.forEach((pathItem, index) => {
      if (!path[index - 1] || !path[index + 1] || !path[index + 2]) return;
      if (pointNodes?.find((pn) => pn?.sweep === pathItem?.data?.id)) return;
      const { x, y, z } = pathItem.data.position;
      const sweepPosition = new THREE.Vector3(x, y, z);
      const prevSignPosition = this.pathDirectionSigns.length
        ? this.pathDirectionSigns[this.pathDirectionSigns.length - 1].position
        : null;
      // To prevent overlapping signs if sweeps are positioned very close
      if (prevSignPosition && prevSignPosition.distanceTo(sweepPosition) < 0.2) return;
      if (sweepPosition.distanceTo(cameraPos) > 40) return;

      const { angle, crossProduct } = getAzimuthAngle(
        path[index - 1].data.position,
        path[index].data.position,
        path[index + 1].data.position,
      );
      if (angle > (Math.PI * 2) / 3) return;

      let texture;
      if (crossProduct < 0) texture = this.directionTextures.left;
      if (crossProduct >= 0) texture = this.directionTextures.right;
      if (!texture) return;

      const aspect = texture.image.width / texture.image.height;
      const [width, height] = aspect >= 1 ? [1, 1 / aspect] : [1 / aspect, 1];

      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
      });
      const geometry = new THREE.PlaneGeometry(width, height);
      const mesh = new THREE.Mesh(geometry, material);
      const title =
        this.inputs.targetObjectData?.tag?.title ||
        this.inputs.targetObjectData?.station?.title ||
        this.inputs.targetObjectData?.title ||
        this.inputs.targetObjectData?.name ||
        '';
      const label = new Label(
        {
          text: title.length > 60 ? `${title.slice(0, 60)}...` : title,
          fontsize: 48,
          width: title.length > 12 ? 460 : 360,
          textColor: {
            r: 0,
            g: 0,
            b: 0,
            a: 1.0,
          },
          backgroundColor: {
            r: 255,
            g: 255,
            b: 255,
            a: 1,
          },
          borderRadius: 0,
        },
        this.context,
      );
      label.root.position.z = 0.05;
      label.root.position.y = 0.015;
      label.root.position.x = title.length > 12 ? 0.12 : 0.16;
      label.pivot.scale.setScalar(title.length > 12 ? 1.25 : 1.6);

      const sign = new THREE.Group(sweepPosition);

      const prevSweepPosition = new THREE.Vector3(
        path[index - 1].data.position.x,
        path[index - 1].data.position.y,
        path[index - 1].data.position.z,
      );
      const signPosition = new THREE.Vector3();
      signPosition.subVectors(sweepPosition, prevSweepPosition).normalize().setLength(0.75);
      signPosition.add(sweepPosition);

      sign.position.copy(signPosition);
      sign.position.y += 1.15;
      sign.add(mesh);
      sign.add(label.root);
      this.pivot.add(sign);

      const prevPosition = path[index - 1].data.position;
      sign.lookAt(prevPosition.x, sign.position.y, prevPosition.z);
      sign.userData = pathItem.data.id;

      this.pathDirectionSigns.push(sign);
    });
  }

  removePathDirectionSigns = () => {
    let currentSweepSign = null;
    // If we stand on sign sweep, don't remove it
    this.pathDirectionSigns.forEach((sign) => {
      if (sign.userData === this.inputs.path[0]?.data?.id) {
        currentSweepSign = sign;
        return;
      }
      this.pivot.remove(sign);
    });

    this.pathDirectionSigns = [];
    if (currentSweepSign) this.pathDirectionSigns = [currentSweepSign];
  };

  stopAnimation() {
    clearTimeout(this.animationId);
    this.animationId = null;
  }

  setVisible(visible) {
    this.inputs.visible = visible;
  }

  reset() {
    this.stopAnimation();
    if (this.sphereArr.length)
      this.sphereArr.forEach((sphere) => {
        sphere.removeFromParent();
      });
    this.sphereArr = [];
  }

  onInputsUpdated(_previousInputs: this['inputs']) {
    this.reset();
    this.removePathDirectionSigns();
    if (this.inputs.path.length > 1) {
      this.createLineFromPath(this.inputs.path);
      this.createPathDirectionSigns(this.inputs.path);
    } else {
      this.emitDistanceEvent(0);
    }
  }

  getCameraAngle() {
    const arrowElement = this.inputs.arrowRef.current;

    this.inputs.camera.getWorldPosition(this.pathSphereHelper);

    if (this.sphereArr.length >= 5) {
      this.pathSphereHelper.x = this.sphereArr[4].position.x;
      this.pathSphereHelper.z = this.sphereArr[4].position.z;
    } else {
      this.pathSphereHelper.x = this.sphereArr[this.sphereArr.length - 1].position.x;
      this.pathSphereHelper.z = this.sphereArr[this.sphereArr.length - 1].position.z;
    }

    this.inputs.camera.getWorldPosition(this.cameraWorld);

    this.cameraDirectionVector.subVectors(this.pathSphereHelper, this.cameraWorld);
    this.cameraDirectionVector.project(this.inputs.camera);

    if (!this.isCameraFocused && this.leftHelperSphere) {
      const targetPathSphere =
        this.sphereArr.length >= 5 ? this.sphereArr[4] : this.sphereArr[this.sphereArr.length - 1];

      this.leftHelperSphere.getWorldPosition(this.leftHelperSphereWorld);
      this.rightHelperSphere.getWorldPosition(this.rightHelperSphereWorld);
      const arrowDirection =
        this.leftHelperSphereWorld.distanceTo(targetPathSphere.position) >
        this.rightHelperSphereWorld.distanceTo(targetPathSphere.position)
          ? 'right'
          : 'left';

      const left = arrowDirection === 'left' ? 40 : window.innerWidth - 40;
      const sign = arrowDirection === 'left' ? 1 : -1;

      this.horizonVector
        .set(0, this.inputs.camera.getWorldPosition(this.cameraWorld).y, 0)
        .normalize();
      this.calculatedCameraDirectionVector
        .set(0, 0, -1)
        .applyQuaternion(this.inputs.camera.getWorldQuaternion(this.cameraWorldQuaternion))
        .normalize();
      const cosAngle = this.calculatedCameraDirectionVector.y;

      let top;
      if (cosAngle < 0) {
        // eslint-disable-next-line no-restricted-properties
        top = (Math.pow(cosAngle, 4) * 0.25 + 0.5) * window.innerHeight;
      } else {
        // eslint-disable-next-line no-restricted-properties
        top = (Math.pow(cosAngle, 4) * -0.25 + 0.5) * window.innerHeight;
      }

      arrowElement.style.left = `${left}px`;
      arrowElement.style.top = `${top}px`;
      arrowElement.style.opacity = 1;
      arrowElement.style.transform = `rotate(${
        (1 - top / (window.innerHeight * 0.5)) * 0.25 * -sign + (sign === -1 ? 0 : 0.5)
      }turn)`;
    } else arrowElement.style.opacity = 0;
  }

  onTick() {
    updateTween();
    this.inputs.camera.matrixWorldInverse.copy(this.inputs.camera.matrixWorld).invert();
    this.cameraViewProjectionMatrix.multiplyMatrices(
      this.inputs.camera.projectionMatrix,
      this.inputs.camera.matrixWorldInverse,
    );
    this.frustum.setFromProjectionMatrix(this.cameraViewProjectionMatrix);
    if (this.sphereArr.length) {
      this.sphereArr.forEach((sphere) => {
        const { x, y, z } = this.inputs.camera.parent.position;
        sphere.lookAt(x, y, z);
      });

      this.isCameraFocused = false;
      this.sphereArr.forEach((sphere) => {
        if (this.frustum.intersectsObject(sphere)) this.isCameraFocused = true;
      });
      if (this.inputs.arrowRef?.current) this.getCameraAngle();
    } else if (this.inputs.arrowRef?.current) {
      const arrowElement = this.inputs.arrowRef.current;
      arrowElement.style.opacity = 0;
    }
  }

  onDestroy() {
    if (this.texture) {
      this.texture.dispose();
      this.texture = null;

      this.directionTextures.left.dispose();
      this.directionTextures.left = null;

      this.directionTextures.right.dispose();
      this.directionTextures.right = null;
    }
  }

  private emitDistanceEvent(distance: number) {
    if (this.inputs.onDistanceChanged) {
      this.inputs.onDistanceChanged(distance % 1 === 0 ? distance.toString() : distance.toFixed(1));
    }
  }
}

export const pathFinderLineComponentType = 'mp.pathFinderLineComponent';
export const makePathFinderLineComponent = (): any => new PathFinderLineComponent();
